/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
    Module No. 17, SBC, Thejaswini Building
    Phase 1, Technopark, Thiruvananthapuram - 695581, 
    Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/


export interface TableColumn {
  Header: string;
  accessor: string;
}

export const columns: TableColumn[] = [
  {
    Header: "tableCommon.sl_no",
    accessor: "slno",
  },
  {
    Header: "mltBioburden.tableColumns.taskId",
    accessor: "taskId",
  },
  {
    Header: "mltBioburden.tableColumns.testId",
    accessor: "id",
  },
  {
    Header: "mltBioburden.tableColumns.sampleType",
    accessor: "sampleType",
  },
  {
    Header: "mltBioburden.tableColumns.sampleName",
    accessor: "sampleName",
  },
  {
    Header: "mltBioburden.tableColumns.batchNo",
    accessor: "batchNo",
  },
  {
    Header: "mltBioburden.tableColumns.testMethod",
    accessor: "testMethod",
  },
  {
    Header: "mltBioburden.tableColumns.requiredPlates",
    accessor: "requiredPlates",
  },
  {
    Header: "mltBioburden.tableColumns.assignedOperator",
    accessor: "assignedOperator",
  },
  {
    Header: "mltBioburden.tableColumns.createdDate",
    accessor: "createdDate",
  },
  {
    Header: "mltBioburden.tableColumns.initiatedDate",
    accessor: "initiatedDate",
  },
  {
    Header: "mltBioburden.tableColumns.currentStage",
    accessor: "currentStage",
  },
  {
    Header: "mltBioburden.tableColumns.status",
    accessor: "status",
  },
  {
    Header: "mltBioburden.tableColumns.action",
    accessor: "action",
  },
];

export interface MLTTestRecord {
  id: string;
  taskId: string;
  arNumber: string;
  sampleType: 'Finished Product' | 'Raw Material' | 'In-Process Bulk' | 'Water for Injection (WFI)' | 'Purified Water';
  sampleSubtype: string;
  sampleId: string;
  sampleName: string;
  productCode: string;
  batchNo: string;
  productionSchedule?: string;
  scheduleLine?: string;
  testMethod: 'Filtration' | 'Plate Count';
  requiredPlates: number;
  assignedOperator: string;
  createdDate: string;
  initiatedDate?: string;
  currentStage: string;
  status: 'Pending Initiation' | 'In Progress' | 'Pending Lab Approval' | 'Pending QA Approval' | 'Completed' | 'Rejected' | 'Cancelled';
  isOverdue: boolean;
  reportAvailable: boolean;
  reportUnavailableReason?: string;
  quantitySampled: string;
  retentionSampleQuantity: string;
  quantityUsedForTest: string;
  quantityDestroyed: string;
  samplingDate: string;
  samplingLocation: string;
  registrationNumber: string;
  registrationDate: string;
  samplePretreatmentRef: string;
  specificationReference: string;
  testedByAnalyst: string;
  checkedBySupervisor: string;
  totalPages: string;
  samplePretreatment: string;
  prepQuantityUsed: string;
  dilutionSeries: string;
  dilutionFactor: string;
  neutralizer: string;
  incubationTAC: string;
  incubationTFC: string;
  tacMediaName: string;
  tacTestMethod: string;
  tacIncubationConditions: string;
  tacSpecLimit: string;
  tacUnit: string;
  tacCalculatedResult: string;
  tacComplianceStatus: string;
  tacPlates: any[];
  tfcMediaName: string;
  tfcTestMethod: string;
  tfcIncubationConditions: string;
  tfcSpecLimit: string;
  tfcUnit: string;
  tfcCalculatedResult: string;
  tfcComplianceStatus: string;
  tfcPlates: any[];
  specifiedOrganisms: any[];
  remarksSection: {
    analystRemarks: {
      text: string;
      author: string;
      timestamp: string;
    };
    reviewerRemarks?: {
      text?: string;
      author?: string;
      timestamp?: string;
    };
    qaRemarks?: {
      text?: string;
      author?: string;
      timestamp?: string;
    };
  };
  reportedOn?: string;
  overallCompliance: {
    tacResult: string;
    tacCompliance: string;
    tfcResult: string;
    tfcCompliance: string;
    specifiedOrganismsCompliance: string;
    overallSampleStatus: string;
    finalDecision: string;
  };
  authorizations: {
    testedBy: {
      name: string;
      userId: string;
      role: string;
      action: string;
      timestamp: string;
      status: string;
    };
    checkedBy: {
      name: string;
      userId: string;
      role: string;
      action: string;
      timestamp: string;
      status: string;
    };
    approvedBy: {
      name: string;
      userId: string;
      role: string;
      action: string;
      timestamp: string;
      status: string;
    };
  };
}

export const SAMPLE_SUBTYPES_MAP: Record<string, string[]> = {
  'Finished Product': [
    'Injectable Solution (Vial)',
    'Injectable Solution (Ampoule)',
    'Injectable Solution (Pre-filled Syringe)',
    'Powder for Injection (Vial)',
    'Lyophilized Powder (Vial)',
    'Tablet',
    'Capsule',
    'Cream/Ointment'
  ],
  'Raw Material': [
    'Active Pharmaceutical Ingredient (API)',
    'Excipient',
    'Packaging Material',
    'Raw Chemical',
    'Solvent'
  ],
  'In-Process Bulk': [
    'Bulk Solution',
    'Bulk Suspension',
    'Bulk Powder',
    'Intermediate Product'
  ],
  'Water for Injection (WFI)': [
    'WFI Storage Tank',
    'WFI Distribution Loop',
    'WFI Point of Use'
  ],
  'Purified Water': [
    'Purified Water Storage',
    'Purified Water Distribution',
    'Purified Water Point of Use'
  ]
};

export const INITIAL_MLT_RECORDS: MLTTestRecord[] = [
  {
    id: 'MLT-2026-00130',
    taskId: 'TSK-MLT-2026-00130',
    arNumber: 'AR-2026-QC-8825',
    sampleType: 'Finished Product',
    sampleSubtype: 'Injectable Solution (Vial)',
    sampleId: 'SMP-2026-FP-0130',
    sampleName: 'Piperacillin & Tazobactam for Injection 4.5g',
    productCode: 'PRD-PIP-450',
    batchNo: 'PIP-261018',
    productionSchedule: 'SCH-2026-0955',
    scheduleLine: 'Line 2 - Aseptic Filling Suite',
    testMethod: 'Filtration',
    requiredPlates: 2,
    assignedOperator: 'Dr. Sarah Jenkins',
    createdDate: '15-Oct-2026 14:30',
    initiatedDate: '16-Oct-2026 09:15',
    currentStage: 'TAC/TFC Incubation & Colony Enumeration',
    status: 'In Progress',
    isOverdue: false,
    reportAvailable: false,
    reportUnavailableReason: 'Test data collection in progress - incubation observations pending',
    quantitySampled: '20 Vials (200 mL)',
    retentionSampleQuantity: '10 Vials',
    quantityUsedForTest: '10 Vials',
    quantityDestroyed: 'Nil',
    samplingDate: '16-Oct-2026 08:00 AM',
    samplingLocation: 'Cleanroom ISO Class 5 / Filling Suite',
    registrationNumber: 'REG-2026-QC-8825',
    registrationDate: '16-Oct-2026 08:30 AM',
    samplePretreatmentRef: 'SOP-QC-PREP-044',
    specificationReference: 'USP <61> / <62>',
    testedByAnalyst: 'Dr. Sarah Jenkins',
    checkedBySupervisor: 'Michael Chen',
    totalPages: '1 of 1',
    samplePretreatment: 'Pre-allocation setup complete',
    prepQuantityUsed: '10.0 mL',
    dilutionSeries: '1:10',
    dilutionFactor: '10⁻¹',
    neutralizer: 'Fluid A Rinse',
    incubationTAC: '30°C - 35°C for 5 Days',
    incubationTFC: '20°C - 25°C for 7 Days',
    tacMediaName: 'Soybean Casein Digest Agar (SCDA)',
    tacTestMethod: 'Filtration',
    tacIncubationConditions: 'Scheduled',
    tacSpecLimit: 'NMT 100 CFU/mL',
    tacUnit: 'CFU/mL',
    tacCalculatedResult: 'Not Started',
    tacComplianceStatus: 'Pending',
    tacPlates: [],
    tfcMediaName: 'Sabouraud Dextrose Agar (SDA)',
    tfcTestMethod: 'Filtration',
    tfcIncubationConditions: 'Scheduled',
    tfcSpecLimit: 'NMT 10 CFU/mL',
    tfcUnit: 'CFU/mL',
    tfcCalculatedResult: 'Not Started',
    tfcComplianceStatus: 'Pending',
    tfcPlates: [],
    specifiedOrganisms: [],
    remarksSection: {
      analystRemarks: {
        text: 'Task initiated by system administrator for Piperacillin & Tazobactam for Injection 4.5g (Batch PIP-261018).',
        author: 'Dr. Sarah Jenkins',
        timestamp: '16-Oct-2026'
      }
    },
    overallCompliance: {
      tacResult: 'Pending',
      tacCompliance: 'Pending',
      tfcResult: 'Pending',
      tfcCompliance: 'Pending',
      specifiedOrganismsCompliance: 'Pending',
      overallSampleStatus: 'Pending Initiation',
      finalDecision: 'Pending Review'
    },
    authorizations: {
      testedBy: {
        name: 'Dr. Sarah Jenkins',
        userId: 'USR-QC-0881',
        role: 'QC Microbiologist',
        action: 'Initiated',
        timestamp: '16-Oct-2026 09:15',
        status: 'Completed'
      },
      checkedBy: {
        name: 'Michael Chen',
        userId: 'USR-QC-0882',
        role: 'QC Supervisor',
        action: 'Pending',
        timestamp: '-',
        status: 'Pending'
      },
      approvedBy: {
        name: 'Pending',
        userId: '-',
        role: 'QA Head',
        action: 'Pending',
        timestamp: '-',
        status: 'Pending'
      }
    }
  },
  {
    id: 'MLT-2026-00129',
    taskId: 'TSK-MLT-2026-00129',
    arNumber: 'AR-2026-QC-8824',
    sampleType: 'Raw Material',
    sampleSubtype: 'Active Pharmaceutical Ingredient (API)',
    sampleId: 'SMP-2026-RM-0129',
    sampleName: 'Vancomycin Hydrochloride for Injection 500mg',
    productCode: 'PRD-VAN-500',
    batchNo: 'VAN-261017',
    testMethod: 'Plate Count',
    requiredPlates: 4,
    assignedOperator: 'Michael Chen',
    createdDate: '14-Oct-2026 11:20',
    initiatedDate: '15-Oct-2026 10:00',
    currentStage: 'Pending Lab Approval',
    status: 'Pending Lab Approval',
    isOverdue: false,
    reportAvailable: false,
    reportUnavailableReason: 'Awaiting laboratory supervisor review and sign-off',
    quantitySampled: '50 g',
    retentionSampleQuantity: '25 g',
    quantityUsedForTest: '25 g',
    quantityDestroyed: 'Nil',
    samplingDate: '15-Oct-2026 09:00 AM',
    samplingLocation: 'Sampling Booth ISO Class 7',
    registrationNumber: 'REG-2026-QC-8824',
    registrationDate: '15-Oct-2026 09:30 AM',
    samplePretreatmentRef: 'SOP-QC-PREP-044',
    specificationReference: 'USP <61> / <62>',
    testedByAnalyst: 'Michael Chen',
    checkedBySupervisor: 'Dr. Sarah Jenkins',
    totalPages: '1 of 1',
    samplePretreatment: 'Pre-allocation setup complete',
    prepQuantityUsed: '25.0 g',
    dilutionSeries: '1:10',
    dilutionFactor: '10⁻¹',
    neutralizer: 'Fluid A Rinse',
    incubationTAC: '30°C - 35°C for 5 Days',
    incubationTFC: '20°C - 25°C for 7 Days',
    tacMediaName: 'Soybean Casein Digest Agar (SCDA)',
    tacTestMethod: 'Plate Count',
    tacIncubationConditions: 'Completed',
    tacSpecLimit: 'NMT 100 CFU/g',
    tacUnit: 'CFU/g',
    tacCalculatedResult: '45 CFU/g',
    tacComplianceStatus: 'Compliant',
    tacPlates: [],
    tfcMediaName: 'Sabouraud Dextrose Agar (SDA)',
    tfcTestMethod: 'Plate Count',
    tfcIncubationConditions: 'Completed',
    tfcSpecLimit: 'NMT 10 CFU/g',
    tfcUnit: 'CFU/g',
    tfcCalculatedResult: '5 CFU/g',
    tfcComplianceStatus: 'Compliant',
    tfcPlates: [],
    specifiedOrganisms: [],
    remarksSection: {
      analystRemarks: {
        text: 'Task initiated by system administrator for Vancomycin Hydrochloride for Injection 500mg (Batch VAN-261017).',
        author: 'Michael Chen',
        timestamp: '15-Oct-2026'
      }
    },
    overallCompliance: {
      tacResult: '45 CFU/g',
      tacCompliance: 'Compliant',
      tfcResult: '5 CFU/g',
      tfcCompliance: 'Compliant',
      specifiedOrganismsCompliance: 'Pending',
      overallSampleStatus: 'Pending Lab Approval',
      finalDecision: 'Pending Review'
    },
    authorizations: {
      testedBy: {
        name: 'Michael Chen',
        userId: 'USR-QC-0882',
        role: 'QC Microbiologist',
        action: 'Completed',
        timestamp: '15-Oct-2026 10:00',
        status: 'Completed'
      },
      checkedBy: {
        name: 'Dr. Sarah Jenkins',
        userId: 'USR-QC-0881',
        role: 'QC Supervisor',
        action: 'Pending',
        timestamp: '-',
        status: 'Pending'
      },
      approvedBy: {
        name: 'Pending',
        userId: '-',
        role: 'QA Head',
        action: 'Pending',
        timestamp: '-',
        status: 'Pending'
      }
    }
  },
  {
    id: 'MLT-2026-00128',
    taskId: 'TSK-MLT-2026-00128',
    arNumber: 'AR-2026-QC-8823',
    sampleType: 'Finished Product',
    sampleSubtype: 'Powder for Injection (Vial)',
    sampleId: 'SMP-2026-FP-0128',
    sampleName: 'Ceftriaxone Sodium for Injection 1g',
    productCode: 'PRD-CEF-1000',
    batchNo: 'CEF-261016',
    productionSchedule: 'SCH-2026-0954',
    scheduleLine: 'Line 1 - Aseptic Filling Suite',
    testMethod: 'Filtration',
    requiredPlates: 2,
    assignedOperator: 'Alex Morgan',
    createdDate: '13-Oct-2026 16:45',
    initiatedDate: '14-Oct-2026 08:30',
    currentStage: 'QA Review & Final Approval',
    status: 'Pending QA Approval',
    isOverdue: false,
    reportAvailable: false,
    reportUnavailableReason: 'Awaiting QA head final review and certificate generation',
    quantitySampled: '15 Vials (150 mL)',
    retentionSampleQuantity: '8 Vials',
    quantityUsedForTest: '7 Vials',
    quantityDestroyed: 'Nil',
    samplingDate: '14-Oct-2026 07:30 AM',
    samplingLocation: 'Cleanroom ISO Class 5 / Filling Suite',
    registrationNumber: 'REG-2026-QC-8823',
    registrationDate: '14-Oct-2026 08:00 AM',
    samplePretreatmentRef: 'SOP-QC-PREP-044',
    specificationReference: 'USP <61> / <62>',
    testedByAnalyst: 'Alex Morgan',
    checkedBySupervisor: 'Elena Rostova',
    totalPages: '1 of 1',
    samplePretreatment: 'Pre-allocation setup complete',
    prepQuantityUsed: '7.0 mL',
    dilutionSeries: '1:10',
    dilutionFactor: '10⁻¹',
    neutralizer: 'Fluid A Rinse',
    incubationTAC: '30°C - 35°C for 5 Days',
    incubationTFC: '20°C - 25°C for 7 Days',
    tacMediaName: 'Soybean Casein Digest Agar (SCDA)',
    tacTestMethod: 'Filtration',
    tacIncubationConditions: 'Completed',
    tacSpecLimit: 'NMT 100 CFU/mL',
    tacUnit: 'CFU/mL',
    tacCalculatedResult: '32 CFU/mL',
    tacComplianceStatus: 'Compliant',
    tacPlates: [],
    tfcMediaName: 'Sabouraud Dextrose Agar (SDA)',
    tfcTestMethod: 'Filtration',
    tfcIncubationConditions: 'Completed',
    tfcSpecLimit: 'NMT 10 CFU/mL',
    tfcUnit: 'CFU/mL',
    tfcCalculatedResult: '3 CFU/mL',
    tfcComplianceStatus: 'Compliant',
    tfcPlates: [],
    specifiedOrganisms: [],
    remarksSection: {
      analystRemarks: {
        text: 'Task initiated by system administrator for Ceftriaxone Sodium for Injection 1g (Batch CEF-261016).',
        author: 'Alex Morgan',
        timestamp: '14-Oct-2026'
      }
    },
    overallCompliance: {
      tacResult: '32 CFU/mL',
      tacCompliance: 'Compliant',
      tfcResult: '3 CFU/mL',
      tfcCompliance: 'Compliant',
      specifiedOrganismsCompliance: 'Pending',
      overallSampleStatus: 'Pending QA Approval',
      finalDecision: 'Pending Review'
    },
    authorizations: {
      testedBy: {
        name: 'Alex Morgan',
        userId: 'USR-QC-0883',
        role: 'QC Microbiologist',
        action: 'Completed',
        timestamp: '14-Oct-2026 08:30',
        status: 'Completed'
      },
      checkedBy: {
        name: 'Elena Rostova',
        userId: 'USR-QC-0884',
        role: 'QC Supervisor',
        action: 'Completed',
        timestamp: '14-Oct-2026 16:45',
        status: 'Completed'
      },
      approvedBy: {
        name: 'Pending',
        userId: '-',
        role: 'QA Head',
        action: 'Pending',
        timestamp: '-',
        status: 'Pending'
      }
    }
  },
  {
    id: 'MLT-2026-00127',
    taskId: 'TSK-MLT-2026-00127',
    arNumber: 'AR-2026-QC-8822',
    sampleType: 'Water for Injection (WFI)',
    sampleSubtype: 'WFI Storage Tank',
    sampleId: 'SMP-2026-WFI-0127',
    sampleName: 'Water for Injection - Storage Tank 1',
    productCode: 'WFI-ST-001',
    batchNo: 'WFI-261015',
    testMethod: 'Filtration',
    requiredPlates: 2,
    assignedOperator: 'Elena Rostova',
    createdDate: '12-Oct-2026 09:15',
    initiatedDate: '13-Oct-2026 08:00',
    currentStage: 'Test Completed & Report Generated',
    status: 'Completed',
    isOverdue: false,
    reportAvailable: true,
    quantitySampled: '500 mL',
    retentionSampleQuantity: '250 mL',
    quantityUsedForTest: '250 mL',
    quantityDestroyed: 'Nil',
    samplingDate: '13-Oct-2026 07:30 AM',
    samplingLocation: 'WFI Distribution Point - Loop A',
    registrationNumber: 'REG-2026-QC-8822',
    registrationDate: '13-Oct-2026 08:00 AM',
    samplePretreatmentRef: 'SOP-QC-PREP-044',
    specificationReference: 'USP <61> / <62>',
    testedByAnalyst: 'Elena Rostova',
    checkedBySupervisor: 'Dr. Sarah Jenkins',
    totalPages: '1 of 1',
    samplePretreatment: 'Pre-allocation setup complete',
    prepQuantityUsed: '250.0 mL',
    dilutionSeries: '1:10',
    dilutionFactor: '10⁻¹',
    neutralizer: 'Fluid A Rinse',
    incubationTAC: '30°C - 35°C for 5 Days',
    incubationTFC: '20°C - 25°C for 7 Days',
    tacMediaName: 'Soybean Casein Digest Agar (SCDA)',
    tacTestMethod: 'Filtration',
    tacIncubationConditions: 'Completed',
    tacSpecLimit: 'NMT 10 CFU/100mL',
    tacUnit: 'CFU/100mL',
    tacCalculatedResult: '2 CFU/100mL',
    tacComplianceStatus: 'Compliant',
    tacPlates: [],
    tfcMediaName: 'Sabouraud Dextrose Agar (SDA)',
    tfcTestMethod: 'Filtration',
    tfcIncubationConditions: 'Completed',
    tfcSpecLimit: 'NMT 1 CFU/100mL',
    tfcUnit: 'CFU/100mL',
    tfcCalculatedResult: '0 CFU/100mL',
    tfcComplianceStatus: 'Compliant',
    tfcPlates: [],
    specifiedOrganisms: [],
    remarksSection: {
      analystRemarks: {
        text: 'Task initiated by system administrator for Water for Injection - Storage Tank 1 (Batch WFI-261015).',
        author: 'Elena Rostova',
        timestamp: '13-Oct-2026'
      }
    },
    overallCompliance: {
      tacResult: '2 CFU/100mL',
      tacCompliance: 'Compliant',
      tfcResult: '0 CFU/100mL',
      tfcCompliance: 'Compliant',
      specifiedOrganismsCompliance: 'Compliant',
      overallSampleStatus: 'Compliant',
      finalDecision: 'Approved'
    },
    authorizations: {
      testedBy: {
        name: 'Elena Rostova',
        userId: 'USR-QC-0884',
        role: 'QC Microbiologist',
        action: 'Completed',
        timestamp: '13-Oct-2026 08:00',
        status: 'Completed'
      },
      checkedBy: {
        name: 'Dr. Sarah Jenkins',
        userId: 'USR-QC-0881',
        role: 'QC Supervisor',
        action: 'Completed',
        timestamp: '13-Oct-2026 16:30',
        status: 'Completed'
      },
      approvedBy: {
        name: 'QA Head',
        userId: 'USR-QA-0001',
        role: 'QA Head',
        action: 'Approved',
        timestamp: '14-Oct-2026 10:00',
        status: 'Completed'
      }
    }
  },
  {
    id: 'MLT-2026-00126',
    taskId: 'TSK-MLT-2026-00126',
    arNumber: 'AR-2026-QC-8821',
    sampleType: 'In-Process Bulk',
    sampleSubtype: 'Bulk Solution',
    sampleId: 'SMP-2026-IP-0126',
    sampleName: 'Bulk Solution - Intermediate Product A',
    productCode: 'INT-PROD-A',
    batchNo: 'INT-261014',
    testMethod: 'Plate Count',
    requiredPlates: 4,
    assignedOperator: 'Dr. Sarah Jenkins',
    createdDate: '11-Oct-2026 13:40',
    initiatedDate: '12-Oct-2026 09:30',
    currentStage: 'TAC/TFC Incubation & Colony Enumeration',
    status: 'In Progress',
    isOverdue: false,
    reportAvailable: false,
    reportUnavailableReason: 'Test data collection in progress - incubation observations pending',
    quantitySampled: '100 mL',
    retentionSampleQuantity: '50 mL',
    quantityUsedForTest: '50 mL',
    quantityDestroyed: 'Nil',
    samplingDate: '12-Oct-2026 08:30 AM',
    samplingLocation: 'Bulk Manufacturing Area ISO Class 7',
    registrationNumber: 'REG-2026-QC-8821',
    registrationDate: '12-Oct-2026 09:00 AM',
    samplePretreatmentRef: 'SOP-QC-PREP-044',
    specificationReference: 'USP <61> / <62>',
    testedByAnalyst: 'Dr. Sarah Jenkins',
    checkedBySupervisor: 'Michael Chen',
    totalPages: '1 of 1',
    samplePretreatment: 'Pre-allocation setup complete',
    prepQuantityUsed: '50.0 mL',
    dilutionSeries: '1:10',
    dilutionFactor: '10⁻¹',
    neutralizer: 'Fluid A Rinse',
    incubationTAC: '30°C - 35°C for 5 Days',
    incubationTFC: '20°C - 25°C for 7 Days',
    tacMediaName: 'Soybean Casein Digest Agar (SCDA)',
    tacTestMethod: 'Plate Count',
    tacIncubationConditions: 'Scheduled',
    tacSpecLimit: 'NMT 100 CFU/mL',
    tacUnit: 'CFU/mL',
    tacCalculatedResult: 'Not Started',
    tacComplianceStatus: 'Pending',
    tacPlates: [],
    tfcMediaName: 'Sabouraud Dextrose Agar (SDA)',
    tfcTestMethod: 'Plate Count',
    tfcIncubationConditions: 'Scheduled',
    tfcSpecLimit: 'NMT 10 CFU/mL',
    tfcUnit: 'CFU/mL',
    tfcCalculatedResult: 'Not Started',
    tfcComplianceStatus: 'Pending',
    tfcPlates: [],
    specifiedOrganisms: [],
    remarksSection: {
      analystRemarks: {
        text: 'Task initiated by system administrator for Bulk Solution - Intermediate Product A (Batch INT-261014).',
        author: 'Dr. Sarah Jenkins',
        timestamp: '12-Oct-2026'
      }
    },
    overallCompliance: {
      tacResult: 'Pending',
      tacCompliance: 'Pending',
      tfcResult: 'Pending',
      tfcCompliance: 'Pending',
      specifiedOrganismsCompliance: 'Pending',
      overallSampleStatus: 'Pending Initiation',
      finalDecision: 'Pending Review'
    },
    authorizations: {
      testedBy: {
        name: 'Dr. Sarah Jenkins',
        userId: 'USR-QC-0881',
        role: 'QC Microbiologist',
        action: 'Initiated',
        timestamp: '12-Oct-2026 09:30',
        status: 'Completed'
      },
      checkedBy: {
        name: 'Michael Chen',
        userId: 'USR-QC-0882',
        role: 'QC Supervisor',
        action: 'Pending',
        timestamp: '-',
        status: 'Pending'
      },
      approvedBy: {
        name: 'Pending',
        userId: '-',
        role: 'QA Head',
        action: 'Pending',
        timestamp: '-',
        status: 'Pending'
      }
    }
  },
  {
    id: 'MLT-2026-00125',
    taskId: 'TSK-MLT-2026-00125',
    arNumber: 'AR-2026-QC-8820',
    sampleType: 'Purified Water',
    sampleSubtype: 'Purified Water Storage',
    sampleId: 'SMP-2026-PW-0125',
    sampleName: 'Purified Water - Storage Tank 2',
    productCode: 'PW-ST-002',
    batchNo: 'PW-261013',
    testMethod: 'Filtration',
    requiredPlates: 2,
    assignedOperator: 'Michael Chen',
    createdDate: '10-Oct-2026 10:25',
    initiatedDate: '11-Oct-2026 08:15',
    currentStage: 'Pending Lab Approval',
    status: 'Pending Lab Approval',
    isOverdue: false,
    reportAvailable: false,
    reportUnavailableReason: 'Awaiting laboratory supervisor review and sign-off',
    quantitySampled: '500 mL',
    retentionSampleQuantity: '250 mL',
    quantityUsedForTest: '250 mL',
    quantityDestroyed: 'Nil',
    samplingDate: '11-Oct-2026 07:45 AM',
    samplingLocation: 'Purified Water Distribution Point',
    registrationNumber: 'REG-2026-QC-8820',
    registrationDate: '11-Oct-2026 08:00 AM',
    samplePretreatmentRef: 'SOP-QC-PREP-044',
    specificationReference: 'USP <61> / <62>',
    testedByAnalyst: 'Michael Chen',
    checkedBySupervisor: 'Alex Morgan',
    totalPages: '1 of 1',
    samplePretreatment: 'Pre-allocation setup complete',
    prepQuantityUsed: '250.0 mL',
    dilutionSeries: '1:10',
    dilutionFactor: '10⁻¹',
    neutralizer: 'Fluid A Rinse',
    incubationTAC: '30°C - 35°C for 5 Days',
    incubationTFC: '20°C - 25°C for 7 Days',
    tacMediaName: 'Soybean Casein Digest Agar (SCDA)',
    tacTestMethod: 'Filtration',
    tacIncubationConditions: 'Completed',
    tacSpecLimit: 'NMT 100 CFU/100mL',
    tacUnit: 'CFU/100mL',
    tacCalculatedResult: '15 CFU/100mL',
    tacComplianceStatus: 'Compliant',
    tacPlates: [],
    tfcMediaName: 'Sabouraud Dextrose Agar (SDA)',
    tfcTestMethod: 'Filtration',
    tfcIncubationConditions: 'Completed',
    tfcSpecLimit: 'NMT 10 CFU/100mL',
    tfcUnit: 'CFU/100mL',
    tfcCalculatedResult: '2 CFU/100mL',
    tfcComplianceStatus: 'Compliant',
    tfcPlates: [],
    specifiedOrganisms: [],
    remarksSection: {
      analystRemarks: {
        text: 'Task initiated by system administrator for Purified Water - Storage Tank 2 (Batch PW-261013).',
        author: 'Michael Chen',
        timestamp: '11-Oct-2026'
      }
    },
    overallCompliance: {
      tacResult: '15 CFU/100mL',
      tacCompliance: 'Compliant',
      tfcResult: '2 CFU/100mL',
      tfcCompliance: 'Compliant',
      specifiedOrganismsCompliance: 'Pending',
      overallSampleStatus: 'Pending Lab Approval',
      finalDecision: 'Pending Review'
    },
    authorizations: {
      testedBy: {
        name: 'Michael Chen',
        userId: 'USR-QC-0882',
        role: 'QC Microbiologist',
        action: 'Completed',
        timestamp: '11-Oct-2026 08:15',
        status: 'Completed'
      },
      checkedBy: {
        name: 'Alex Morgan',
        userId: 'USR-QC-0883',
        role: 'QC Supervisor',
        action: 'Pending',
        timestamp: '-',
        status: 'Pending'
      },
      approvedBy: {
        name: 'Pending',
        userId: '-',
        role: 'QA Head',
        action: 'Pending',
        timestamp: '-',
        status: 'Pending'
      }
    }
  },
  {
    id: 'MLT-2026-00124',
    taskId: 'TSK-MLT-2026-00124',
    arNumber: 'AR-2026-QC-8819',
    sampleType: 'Finished Product',
    sampleSubtype: 'Injectable Solution (Ampoule)',
    sampleId: 'SMP-2026-FP-0124',
    sampleName: 'Amoxicillin Sodium for Injection 1g',
    productCode: 'PRD-AMX-1000',
    batchNo: 'AMX-261012',
    productionSchedule: 'SCH-2026-0953',
    scheduleLine: 'Line 3 - Aseptic Filling Suite',
    testMethod: 'Plate Count',
    requiredPlates: 4,
    assignedOperator: 'Alex Morgan',
    createdDate: '09-Oct-2026 15:10',
    initiatedDate: '10-Oct-2026 09:00',
    currentStage: 'Test Completed & Report Generated',
    status: 'Completed',
    isOverdue: false,
    reportAvailable: true,
    quantitySampled: '25 Ampoules (250 mL)',
    retentionSampleQuantity: '12 Ampoules',
    quantityUsedForTest: '13 Ampoules',
    quantityDestroyed: 'Nil',
    samplingDate: '10-Oct-2026 08:00 AM',
    samplingLocation: 'Cleanroom ISO Class 5 / Filling Suite',
    registrationNumber: 'REG-2026-QC-8819',
    registrationDate: '10-Oct-2026 08:30 AM',
    samplePretreatmentRef: 'SOP-QC-PREP-044',
    specificationReference: 'USP <61> / <62>',
    testedByAnalyst: 'Alex Morgan',
    checkedBySupervisor: 'Elena Rostova',
    totalPages: '1 of 1',
    samplePretreatment: 'Pre-allocation setup complete',
    prepQuantityUsed: '13.0 mL',
    dilutionSeries: '1:10',
    dilutionFactor: '10⁻¹',
    neutralizer: 'Fluid A Rinse',
    incubationTAC: '30°C - 35°C for 5 Days',
    incubationTFC: '20°C - 25°C for 7 Days',
    tacMediaName: 'Soybean Casein Digest Agar (SCDA)',
    tacTestMethod: 'Plate Count',
    tacIncubationConditions: 'Completed',
    tacSpecLimit: 'NMT 100 CFU/mL',
    tacUnit: 'CFU/mL',
    tacCalculatedResult: '28 CFU/mL',
    tacComplianceStatus: 'Compliant',
    tacPlates: [],
    tfcMediaName: 'Sabouraud Dextrose Agar (SDA)',
    tfcTestMethod: 'Plate Count',
    tfcIncubationConditions: 'Completed',
    tfcSpecLimit: 'NMT 10 CFU/mL',
    tfcUnit: 'CFU/mL',
    tfcCalculatedResult: '4 CFU/mL',
    tfcComplianceStatus: 'Compliant',
    tfcPlates: [],
    specifiedOrganisms: [],
    remarksSection: {
      analystRemarks: {
        text: 'Task initiated by system administrator for Amoxicillin Sodium for Injection 1g (Batch AMX-261012).',
        author: 'Alex Morgan',
        timestamp: '10-Oct-2026'
      }
    },
    overallCompliance: {
      tacResult: '28 CFU/mL',
      tacCompliance: 'Compliant',
      tfcResult: '4 CFU/mL',
      tfcCompliance: 'Compliant',
      specifiedOrganismsCompliance: 'Compliant',
      overallSampleStatus: 'Compliant',
      finalDecision: 'Approved'
    },
    authorizations: {
      testedBy: {
        name: 'Alex Morgan',
        userId: 'USR-QC-0883',
        role: 'QC Microbiologist',
        action: 'Completed',
        timestamp: '10-Oct-2026 09:00',
        status: 'Completed'
      },
      checkedBy: {
        name: 'Elena Rostova',
        userId: 'USR-QC-0884',
        role: 'QC Supervisor',
        action: 'Completed',
        timestamp: '10-Oct-2026 17:30',
        status: 'Completed'
      },
      approvedBy: {
        name: 'QA Head',
        userId: 'USR-QA-0001',
        role: 'QA Head',
        action: 'Approved',
        timestamp: '11-Oct-2026 11:00',
        status: 'Completed'
      }
    }
  },
  {
    id: 'MLT-2026-00123',
    taskId: 'TSK-MLT-2026-00123',
    arNumber: 'AR-2026-QC-8818',
    sampleType: 'Raw Material',
    sampleSubtype: 'Excipient',
    sampleId: 'SMP-2026-RM-0123',
    sampleName: 'Lactose Monohydrate EP',
    productCode: 'EXC-LAC-001',
    batchNo: 'LAC-261011',
    testMethod: 'Plate Count',
    requiredPlates: 4,
    assignedOperator: 'Elena Rostova',
    createdDate: '08-Oct-2026 14:55',
    initiatedDate: '09-Oct-2026 10:45',
    currentStage: 'Pending Initiation',
    status: 'Pending Initiation',
    isOverdue: false,
    reportAvailable: false,
    reportUnavailableReason: 'Task is pending initiation. Operator must verify sample receipt and initiate testing before analytical data can be generated.',
    quantitySampled: '100 g',
    retentionSampleQuantity: '50 g',
    quantityUsedForTest: '50 g',
    quantityDestroyed: 'Nil',
    samplingDate: '09-Oct-2026 10:00 AM',
    samplingLocation: 'Sampling Booth ISO Class 7',
    registrationNumber: 'REG-2026-QC-8818',
    registrationDate: '09-Oct-2026 10:30 AM',
    samplePretreatmentRef: 'SOP-QC-PREP-044',
    specificationReference: 'USP <61> / <62>',
    testedByAnalyst: 'Elena Rostova',
    checkedBySupervisor: '-',
    totalPages: '1 of 1',
    samplePretreatment: 'Pre-allocation setup complete',
    prepQuantityUsed: '50.0 g',
    dilutionSeries: '1:10',
    dilutionFactor: '10⁻¹',
    neutralizer: 'Fluid A Rinse',
    incubationTAC: '30°C - 35°C for 5 Days',
    incubationTFC: '20°C - 25°C for 7 Days',
    tacMediaName: 'Soybean Casein Digest Agar (SCDA)',
    tacTestMethod: 'Plate Count',
    tacIncubationConditions: 'Scheduled',
    tacSpecLimit: 'NMT 1000 CFU/g',
    tacUnit: 'CFU/g',
    tacCalculatedResult: 'Not Started',
    tacComplianceStatus: 'Pending',
    tacPlates: [],
    tfcMediaName: 'Sabouraud Dextrose Agar (SDA)',
    tfcTestMethod: 'Plate Count',
    tfcIncubationConditions: 'Scheduled',
    tfcSpecLimit: 'NMT 100 CFU/g',
    tfcUnit: 'CFU/g',
    tfcCalculatedResult: 'Not Started',
    tfcComplianceStatus: 'Pending',
    tfcPlates: [],
    specifiedOrganisms: [],
    remarksSection: {
      analystRemarks: {
        text: 'Task initiated by system administrator for Lactose Monohydrate EP (Batch LAC-261011).',
        author: 'Elena Rostova',
        timestamp: '09-Oct-2026'
      }
    },
    overallCompliance: {
      tacResult: 'Pending',
      tacCompliance: 'Pending',
      tfcResult: 'Pending',
      tfcCompliance: 'Pending',
      specifiedOrganismsCompliance: 'Pending',
      overallSampleStatus: 'Pending Initiation',
      finalDecision: 'Pending Review'
    },
    authorizations: {
      testedBy: {
        name: 'Elena Rostova',
        userId: 'USR-QC-0884',
        role: 'QC Microbiologist',
        action: 'Pending Initiation',
        timestamp: '-',
        status: 'Pending'
      },
      checkedBy: {
        name: 'Pending',
        userId: '-',
        role: 'QC Supervisor',
        action: 'Pending',
        timestamp: '-',
        status: 'Pending'
      },
      approvedBy: {
        name: 'Pending',
        userId: '-',
        role: 'QA Head',
        action: 'Pending',
        timestamp: '-',
        status: 'Pending'
      }
    }
  },
  {
    id: 'MLT-2026-00122',
    taskId: 'TSK-MLT-2026-00122',
    arNumber: 'AR-2026-QC-8817',
    sampleType: 'Finished Product',
    sampleSubtype: 'Injectable Solution (Pre-filled Syringe)',
    sampleId: 'SMP-2026-FP-0122',
    sampleName: 'Heparin Sodium Injection 5000 IU/mL',
    productCode: 'PRD-HEP-5000',
    batchNo: 'HEP-261010',
    productionSchedule: 'SCH-2026-0952',
    scheduleLine: 'Line 4 - Aseptic Filling Suite',
    testMethod: 'Filtration',
    requiredPlates: 2,
    assignedOperator: 'Dr. Sarah Jenkins',
    createdDate: '07-Oct-2026 11:30',
    initiatedDate: '08-Oct-2026 08:45',
    currentStage: 'TAC/TFC Incubation & Colony Enumeration',
    status: 'In Progress',
    isOverdue: false,
    reportAvailable: false,
    reportUnavailableReason: 'Test data collection in progress - incubation observations pending',
    quantitySampled: '30 Syringes (300 mL)',
    retentionSampleQuantity: '15 Syringes',
    quantityUsedForTest: '15 Syringes',
    quantityDestroyed: 'Nil',
    samplingDate: '08-Oct-2026 08:00 AM',
    samplingLocation: 'Cleanroom ISO Class 5 / Filling Suite',
    registrationNumber: 'REG-2026-QC-8817',
    registrationDate: '08-Oct-2026 08:30 AM',
    samplePretreatmentRef: 'SOP-QC-PREP-044',
    specificationReference: 'USP <61> / <62>',
    testedByAnalyst: 'Dr. Sarah Jenkins',
    checkedBySupervisor: 'Michael Chen',
    totalPages: '1 of 1',
    samplePretreatment: 'Pre-allocation setup complete',
    prepQuantityUsed: '15.0 mL',
    dilutionSeries: '1:10',
    dilutionFactor: '10⁻¹',
    neutralizer: 'Fluid A Rinse',
    incubationTAC: '30°C - 35°C for 5 Days',
    incubationTFC: '20°C - 25°C for 7 Days',
    tacMediaName: 'Soybean Casein Digest Agar (SCDA)',
    tacTestMethod: 'Filtration',
    tacIncubationConditions: 'Scheduled',
    tacSpecLimit: 'NMT 100 CFU/mL',
    tacUnit: 'CFU/mL',
    tacCalculatedResult: 'Not Started',
    tacComplianceStatus: 'Pending',
    tacPlates: [],
    tfcMediaName: 'Sabouraud Dextrose Agar (SDA)',
    tfcTestMethod: 'Filtration',
    tfcIncubationConditions: 'Scheduled',
    tfcSpecLimit: 'NMT 10 CFU/mL',
    tfcUnit: 'CFU/mL',
    tfcCalculatedResult: 'Not Started',
    tfcComplianceStatus: 'Pending',
    tfcPlates: [],
    specifiedOrganisms: [],
    remarksSection: {
      analystRemarks: {
        text: 'Task initiated by system administrator for Heparin Sodium Injection 5000 IU/mL (Batch HEP-261010).',
        author: 'Dr. Sarah Jenkins',
        timestamp: '08-Oct-2026'
      }
    },
    overallCompliance: {
      tacResult: 'Pending',
      tacCompliance: 'Pending',
      tfcResult: 'Pending',
      tfcCompliance: 'Pending',
      specifiedOrganismsCompliance: 'Pending',
      overallSampleStatus: 'Pending Initiation',
      finalDecision: 'Pending Review'
    },
    authorizations: {
      testedBy: {
        name: 'Dr. Sarah Jenkins',
        userId: 'USR-QC-0881',
        role: 'QC Microbiologist',
        action: 'Initiated',
        timestamp: '08-Oct-2026 08:45',
        status: 'Completed'
      },
      checkedBy: {
        name: 'Michael Chen',
        userId: 'USR-QC-0882',
        role: 'QC Supervisor',
        action: 'Pending',
        timestamp: '-',
        status: 'Pending'
      },
      approvedBy: {
        name: 'Pending',
        userId: '-',
        role: 'QA Head',
        action: 'Pending',
        timestamp: '-',
        status: 'Pending'
      }
    }
  },
  {
    id: 'MLT-2026-00121',
    taskId: 'TSK-MLT-2026-00121',
    arNumber: 'AR-2026-QC-8816',
    sampleType: 'In-Process Bulk',
    sampleSubtype: 'Bulk Powder',
    sampleId: 'SMP-2026-IP-0121',
    sampleName: 'Bulk Powder - Intermediate Product B',
    productCode: 'INT-PROD-B',
    batchNo: 'INT-261009',
    testMethod: 'Plate Count',
    requiredPlates: 4,
    assignedOperator: 'Michael Chen',
    createdDate: '06-Oct-2026 16:20',
    initiatedDate: '07-Oct-2026 09:15',
    currentStage: 'Pending QA Approval',
    status: 'Pending QA Approval',
    isOverdue: false,
    reportAvailable: false,
    reportUnavailableReason: 'Awaiting QA head final review and certificate generation',
    quantitySampled: '75 g',
    retentionSampleQuantity: '37.5 g',
    quantityUsedForTest: '37.5 g',
    quantityDestroyed: 'Nil',
    samplingDate: '07-Oct-2026 08:30 AM',
    samplingLocation: 'Bulk Manufacturing Area ISO Class 7',
    registrationNumber: 'REG-2026-QC-8816',
    registrationDate: '07-Oct-2026 09:00 AM',
    samplePretreatmentRef: 'SOP-QC-PREP-044',
    specificationReference: 'USP <61> / <62>',
    testedByAnalyst: 'Michael Chen',
    checkedBySupervisor: 'Alex Morgan',
    totalPages: '1 of 1',
    samplePretreatment: 'Pre-allocation setup complete',
    prepQuantityUsed: '37.5 g',
    dilutionSeries: '1:10',
    dilutionFactor: '10⁻¹',
    neutralizer: 'Fluid A Rinse',
    incubationTAC: '30°C - 35°C for 5 Days',
    incubationTFC: '20°C - 25°C for 7 Days',
    tacMediaName: 'Soybean Casein Digest Agar (SCDA)',
    tacTestMethod: 'Plate Count',
    tacIncubationConditions: 'Completed',
    tacSpecLimit: 'NMT 100 CFU/g',
    tacUnit: 'CFU/g',
    tacCalculatedResult: '52 CFU/g',
    tacComplianceStatus: 'Compliant',
    tacPlates: [],
    tfcMediaName: 'Sabouraud Dextrose Agar (SDA)',
    tfcTestMethod: 'Plate Count',
    tfcIncubationConditions: 'Completed',
    tfcSpecLimit: 'NMT 10 CFU/g',
    tfcUnit: 'CFU/g',
    tfcCalculatedResult: '6 CFU/g',
    tfcComplianceStatus: 'Compliant',
    tfcPlates: [],
    specifiedOrganisms: [],
    remarksSection: {
      analystRemarks: {
        text: 'Task initiated by system administrator for Bulk Powder - Intermediate Product B (Batch INT-261009).',
        author: 'Michael Chen',
        timestamp: '07-Oct-2026'
      }
    },
    overallCompliance: {
      tacResult: '52 CFU/g',
      tacCompliance: 'Compliant',
      tfcResult: '6 CFU/g',
      tfcCompliance: 'Compliant',
      specifiedOrganismsCompliance: 'Pending',
      overallSampleStatus: 'Pending QA Approval',
      finalDecision: 'Pending Review'
    },
    authorizations: {
      testedBy: {
        name: 'Michael Chen',
        userId: 'USR-QC-0882',
        role: 'QC Microbiologist',
        action: 'Completed',
        timestamp: '07-Oct-2026 09:15',
        status: 'Completed'
      },
      checkedBy: {
        name: 'Alex Morgan',
        userId: 'USR-QC-0883',
        role: 'QC Supervisor',
        action: 'Completed',
        timestamp: '07-Oct-2026 17:00',
        status: 'Completed'
      },
      approvedBy: {
        name: 'Pending',
        userId: '-',
        role: 'QA Head',
        action: 'Pending',
        timestamp: '-',
        status: 'Pending'
      }
    }
  }
];
